// Tracks which Big Things the player has unlocked. Decoupled from the
// invention solve set so we can replay or migrate independently.

import { BIG_THINGS, type BigThing } from '../data/bigThings';

const KEY = 'pa-bigthings-unlocked';

function loadUnlockedSet(): Set<string> {
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return new Set(JSON.parse(raw) as string[]);
  } catch {}
  return new Set();
}
function saveUnlockedSet(s: Set<string>): void {
  try { localStorage.setItem(KEY, JSON.stringify([...s])); } catch {}
}

export function unlockedIds(): Set<string> {
  return loadUnlockedSet();
}

export function isUnlocked(id: string): boolean {
  return loadUnlockedSet().has(id);
}

export function markUnlocked(id: string): void {
  const s = loadUnlockedSet();
  s.add(id);
  saveUnlockedSet(s);
}

export function resetUnlocked(): void {
  try { localStorage.removeItem(KEY); } catch {}
}

/**
 * Returns Big Things that should fire as a milestone right now —
 * i.e. their threshold is met by `solvedCount` but they haven't yet
 * been unlocked locally.
 */
export function newlyEarned(solvedCount: number): BigThing[] {
  const u = loadUnlockedSet();
  return BIG_THINGS.filter((bt) => bt.milestone <= solvedCount && !u.has(bt.id));
}

/** Next Big Thing the player is working toward (or null if all done). */
export function nextTarget(solvedCount: number): BigThing | null {
  const next = BIG_THINGS.find((bt) => bt.milestone > solvedCount);
  return next ?? null;
}

/**
 * Progress within the current "cycle" — i.e. solves between the previously
 * earned monument and the next one. Used to drive the segmented bar UI.
 *   total  = solves needed in this cycle (typically 5, last cycle 4)
 *   filled = how many of those have been earned so far
 */
export function cycleProgress(
  solved: number,
): { filled: number; total: number; nextMonument: BigThing | null } {
  const next = nextTarget(solved);
  if (!next) return { filled: 0, total: 0, nextMonument: null };
  const milestones = BIG_THINGS.map((bt) => bt.milestone).sort((a, b) => a - b);
  const idx = milestones.indexOf(next.milestone);
  const prev = idx > 0 ? milestones[idx - 1] : 0;
  const total = next.milestone - prev;
  const filled = Math.max(0, Math.min(total, solved - prev));
  return { filled, total, nextMonument: next };
}
